import { endOfMonth, endOfWeek, format, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import type { JournalEntryType, JournalEntryWithAuthor } from '@/hooks/useJournal';

export const JOURNAL_DATE_PRESETS = [
  'all',
  'today',
  'this_week',
  'this_month',
  'last_3_months',
  'custom',
] as const;

export type JournalDatePreset = (typeof JOURNAL_DATE_PRESETS)[number];

export const JOURNAL_STATUS_FILTERS = ['all', 'draft', 'finalized', 'discarded'] as const;
export type JournalStatusFilter = (typeof JOURNAL_STATUS_FILTERS)[number];

/**
 * History "All types" options. Do not dump `journal_entry_type` into this list.
 * That enum is a technical column (default `generic`; `dentist` only from the
 * dental form; `emdr_session` / `psychology_session` are unused and rejected by
 * create_journal_entry). It is not a user-facing journal category, appointment
 * service, or profession. Profession-specific types wait on profession config.
 */
export const JOURNAL_TYPE_FILTERS = ['all'] as const;

export type JournalTypeFilter = (typeof JOURNAL_TYPE_FILTERS)[number];

export const JOURNAL_ATTACHMENT_FILTERS = ['all', 'with', 'without'] as const;
export type JournalAttachmentFilter = (typeof JOURNAL_ATTACHMENT_FILTERS)[number];

export interface JournalHistoryFilterState {
  datePreset: JournalDatePreset;
  customFrom: string;
  customTo: string;
  type: JournalTypeFilter;
  status: JournalStatusFilter;
  attachment: JournalAttachmentFilter;
}

export const EMPTY_JOURNAL_HISTORY_FILTERS: JournalHistoryFilterState = {
  datePreset: 'all',
  customFrom: '',
  customTo: '',
  type: 'all',
  status: 'all',
  attachment: 'all',
};

export type JournalFilterChipKey = 'date' | 'type' | 'status' | 'attachment';

export function isJournalDateFilterActive(
  state: Pick<JournalHistoryFilterState, 'datePreset'>,
): boolean {
  return state.datePreset !== 'all';
}

export function activeJournalFilterKeys(state: JournalHistoryFilterState): JournalFilterChipKey[] {
  const keys: JournalFilterChipKey[] = [];
  if (isJournalDateFilterActive(state)) keys.push('date');
  if (state.type !== 'all') keys.push('type');
  if (state.status !== 'all') keys.push('status');
  if (state.attachment !== 'all') keys.push('attachment');
  return keys;
}

export function countActiveJournalFilters(state: JournalHistoryFilterState): number {
  return activeJournalFilterKeys(state).length;
}

export function clearJournalFilter(
  state: JournalHistoryFilterState,
  key: JournalFilterChipKey,
): JournalHistoryFilterState {
  switch (key) {
    case 'date':
      return { ...state, datePreset: 'all', customFrom: '', customTo: '' };
    case 'type':
      return { ...state, type: 'all' };
    case 'status':
      return { ...state, status: 'all' };
    case 'attachment':
      return { ...state, attachment: 'all' };
    default: {
      const _exhaustive: never = key;
      return _exhaustive;
    }
  }
}

const WEEK_OPTIONS = { weekStartsOn: 1 as const };

function ymd(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

export function resolveJournalDateRange(
  preset: JournalDatePreset,
  customFrom: string,
  customTo: string,
  now: Date,
): { from?: string; to?: string } {
  switch (preset) {
    case 'all':
      return {};
    case 'today': {
      const day = ymd(now);
      return { from: day, to: day };
    }
    case 'this_week':
      return {
        from: ymd(startOfWeek(now, WEEK_OPTIONS)),
        to: ymd(endOfWeek(now, WEEK_OPTIONS)),
      };
    case 'this_month':
      return { from: ymd(startOfMonth(now)), to: ymd(endOfMonth(now)) };
    case 'last_3_months':
      return { from: ymd(subMonths(now, 3)), to: ymd(now) };
    case 'custom': {
      const range: { from?: string; to?: string } = {};
      if (customFrom) range.from = customFrom;
      if (customTo) range.to = customTo;
      return range;
    }
  }
}

export function journalEntryStatus(entry: {
  discarded_at?: string | null;
  archived_at?: string | null;
  finalized_at: string | null;
}): Exclude<JournalStatusFilter, 'all'> {
  if (entry.discarded_at) return 'discarded';
  if (entry.finalized_at) return 'finalized';
  return 'draft';
}

export function matchesJournalSearch(
  entry: Pick<JournalEntryWithAuthor, 'title' | 'content_md'>,
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const title = (entry.title ?? '').toLowerCase();
  const content = (entry.content_md ?? '').toLowerCase();
  return title.includes(needle) || content.includes(needle);
}

export function matchesJournalStatus(
  entry: {
    discarded_at?: string | null;
    archived_at?: string | null;
    finalized_at: string | null;
  },
  status: JournalStatusFilter,
): boolean {
  if (status === 'all') return !entry.discarded_at;
  return journalEntryStatus(entry) === status;
}

export function matchesJournalType(
  _entry: { entry_type: JournalEntryType },
  type: JournalTypeFilter,
): boolean {
  return type === 'all';
}

export function countAttachmentsByEntryId(
  rows: Array<{ journal_entry_id: string }>,
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const row of rows) {
    counts[row.journal_entry_id] = (counts[row.journal_entry_id] ?? 0) + 1;
  }
  return counts;
}

export function matchesJournalAttachments(count: number, filter: JournalAttachmentFilter): boolean {
  if (filter === 'with') return count > 0;
  if (filter === 'without') return count < 1;
  return true;
}

export interface JournalLinkedAppointment {
  starts_at: string;
  service_name: string | null;
  clinician_name: string | null;
}

export function journalDisplayAppointment(
  entry: {
    appointment_starts_at_snapshot?: string | null;
    appointment_service_name_snapshot?: string | null;
    author_name_snapshot?: string | null;
    finalized_at: string | null;
  },
  live?: JournalLinkedAppointment | null,
): JournalLinkedAppointment | null {
  if (entry.finalized_at) {
    const starts = entry.appointment_starts_at_snapshot ?? live?.starts_at ?? null;
    if (!starts && !entry.appointment_service_name_snapshot && !entry.author_name_snapshot) {
      return live ?? null;
    }
    return {
      starts_at: starts ?? live?.starts_at ?? entry.finalized_at,
      service_name: entry.appointment_service_name_snapshot ?? live?.service_name ?? null,
      clinician_name: entry.author_name_snapshot ?? live?.clinician_name ?? null,
    };
  }
  return live ?? null;
}

export function journalKindLabel(input: {
  entryType: JournalEntryType;
  serviceName?: string | null;
  typeLabel: (type: JournalEntryType) => string;
}): string | null {
  const service = input.serviceName?.trim();
  if (service) return service;
  if (input.entryType === 'generic') return null;
  return input.typeLabel(input.entryType);
}

export function filterJournalEntries(
  entries: JournalEntryWithAuthor[],
  opts: {
    search: string;
    status: JournalStatusFilter;
    type: JournalTypeFilter;
    attachment?: JournalAttachmentFilter;
    attachmentCounts?: Record<string, number>;
  },
): JournalEntryWithAuthor[] {
  const attachment = opts.attachment ?? 'all';
  const attachmentCounts = opts.attachmentCounts ?? {};
  return entries.filter(
    (entry) =>
      matchesJournalSearch(entry, opts.search) &&
      matchesJournalStatus(entry, opts.status) &&
      matchesJournalType(entry, opts.type) &&
      matchesJournalAttachments(attachmentCounts[entry.id] ?? 0, attachment),
  );
}
